import React from 'react';
import { 
    Table, 
    Tag, 
    Space, 
    Button, 
    Tooltip, 
    Popconfirm,
    Typography 
} from 'antd';
import { 
    EditOutlined, 
    DeleteOutlined, 
    CheckCircleOutlined,
    StopOutlined,
    PlusCircleOutlined,
    MinusCircleOutlined,
    SwapOutlined,
    BookOutlined,
    LockOutlined,
    RiseOutlined,
    FallOutlined,
    RetweetOutlined,
    ToolOutlined,
    MoreOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { TransactionMenuConfigDTO } from '../types';

const { Text } = Typography;

interface TransactionMenuTableProps {
    menus: TransactionMenuConfigDTO[];
    isLoading: boolean;
    onEdit: (menu: TransactionMenuConfigDTO) => void;
    onDelete: (id: number) => void;
    onActivate: (id: number) => void;
    onDeactivate: (id: number) => void;
}

const iconMap: Record<string, React.ReactNode> = {
    PlusCircleOutlined: <PlusCircleOutlined />,
    MinusCircleOutlined: <MinusCircleOutlined />,
    SwapOutlined: <SwapOutlined />,
    BookOutlined: <BookOutlined />,
    LockOutlined: <LockOutlined />,
    RiseOutlined: <RiseOutlined />,
    FallOutlined: <FallOutlined />,
    RetweetOutlined: <RetweetOutlined />,
    ToolOutlined: <ToolOutlined />,
    MoreOutlined: <MoreOutlined />
};

const typeColorMap: Record<string, string> = {
    SAVINGS_DEPOSIT: 'green',
    SAVINGS_WITHDRAWAL: 'orange',
    SHARES_DEPOSIT: 'blue',
    SHARES_WITHDRAWAL: 'purple',
    INCOME_ENTRY: 'cyan',
    EXPENSE_ENTRY: 'red',
    GENERAL_JOURNAL: 'geekblue',
    LOAN_REPAYMENT: 'gold',
    FIXED_DEPOSIT: 'magenta',
    TRANSFER: 'lime',
    ERROR_CORRECTION: 'volcano',
    OTHER: 'default'
};

export const TransactionMenuTable: React.FC<TransactionMenuTableProps> = ({
    menus,
    isLoading,
    onEdit,
    onDelete,
    onActivate,
    onDeactivate
}) => {
    const columns: ColumnsType<TransactionMenuConfigDTO> = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (title: string, record) => (
                <Space>
                    {record.icon && iconMap[record.icon] ? iconMap[record.icon] : <MoreOutlined />}
                    <Text strong>{title}</Text>
                </Space>
            ),
            sorter: (a, b) => a.title.localeCompare(b.title)
        },
        {
            title: 'Type',
            dataIndex: 'transactionType',
            key: 'transactionType',
            render: (type: string, record) => (
                <Tag color={typeColorMap[type] || 'default'}>
                    {record.transactionTypeDisplayName || type}
                </Tag>
            ),
            filters: Object.entries(typeColorMap).map(([key]) => ({
                text: key.replace(/_/g, ' '),
                value: key
            })),
            onFilter: (value, record) => record.transactionType === value
        },
        {
            title: 'Debit',
            key: 'debit',
            render: (_, record) => (
                <Text type="secondary">
                    {record.debitGlNo || '-'}/{record.debitAcNo || '-'}
                </Text>
            )
        },
        {
            title: 'Credit',
            key: 'credit',
            render: (_, record) => (
                <Text type="secondary">
                    {record.creditGlNo || '-'}/{record.creditAcNo || '-'}
                </Text>
            )
        },
        {
            title: 'TRX Code',
            dataIndex: 'trxCode',
            key: 'trxCode',
            render: (code: string) => code || '-'
        },
        {
            title: 'Receipt',
            dataIndex: 'printReceipt',
            key: 'printReceipt',
            render: (printReceipt: boolean) => (
                printReceipt ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean) => (
                active 
                    ? <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>
                    : <Tag color="default" icon={<StopOutlined />}>Inactive</Tag>
            ),
            filters: [
                { text: 'Active', value: true },
                { text: 'Inactive', value: false }
            ],
            onFilter: (value, record) => record.active === value
        },
        {
            title: 'Permission',
            dataIndex: 'permissionCode',
            key: 'permissionCode',
            render: (code: string) => (
                <Text code copyable={{ text: code }}>
                    {code}
                </Text>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record)}
                        />
                    </Tooltip>
                    
                    {record.active ? (
                        <Tooltip title="Deactivate">
                            <Popconfirm
                                title="Deactivate this menu?"
                                description="Users will no longer see this menu."
                                onConfirm={() => record.id && onDeactivate(record.id)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button
                                    type="text"
                                    icon={<StopOutlined />}
                                    danger
                                />
                            </Popconfirm>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Activate">
                            <Button
                                type="text"
                                icon={<CheckCircleOutlined />}
                                onClick={() => record.id && onActivate(record.id)}
                                style={{ color: '#52c41a' }}
                            />
                        </Tooltip>
                    )}
                    
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Delete this menu?"
                            description="This action cannot be undone. The associated permission will also be deleted."
                            onConfirm={() => record.id && onDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <Table
            columns={columns}
            dataSource={menus}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 1200 }}
            pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} menus`
            }}
        />
    );
};
